/**
 * Explosions: everything inside the radius disappears (a choice of the author, against the GDD, where the
 * rock of an explosion turns into sand), except water, which boils into steam; the edges catch fire
 * (sim's blastCrater). Units take damage that decreases with distance, and are thrown away from the centre.
 */
import { blastCrater, isqrt } from '@invaders/sim';
import { FP, type GameState } from './state';
import { UNIT_W, damageUnit, unitCenter } from './unit';
import { blastSteles } from './legacy';
import { blastChests } from './chests';
import { blastBatteries } from './battery';
import { blastBarrels } from './barrel';

/** Speed given to a unit at the centre of a blast, in cells per tick (fixed-point FP). */
export const BLAST_KNOCKBACK = 5 * FP;

/** `knock`: percent of BLAST_KNOCKBACK given to what it throws away (Weapon.knock). */
export function explode(state: GameState, cx: number, cy: number, radius: number, maxDamage: number, knock = 100): void {
  blastCrater(state.world, cx, cy, radius);
  state.events.push({ type: 'explosion', x: cx, y: cy, radius });
  blastSteles(state, cx, cy, radius);
  const blow = Math.trunc((BLAST_KNOCKBACK * knock) / 100);
  blastChests(state, cx, cy, radius, blow);
  blastBatteries(state, cx, cy, radius);
  blastBarrels(state, cx, cy, radius);

  for (const unit of state.units) {
    if (!unit.alive) continue;
    const [ux, uy] = unitCenter(unit);
    let dx = ux - cx;
    let dy = uy - cy;
    let d = isqrt(dx * dx + dy * dy);
    // Distance to the edge of the body rather than its centre: a blast at the feet hits fully.
    const reach = d - (UNIT_W >> 1) > 0 ? d - (UNIT_W >> 1) : 0;
    if (reach >= radius) continue;
    if (d === 0) { dx = 0; dy = -1; d = 1; }
    const falloff = radius - reach;
    let damage = Math.trunc((maxDamage * falloff) / radius);
    // GDD "Bris de glace": the blast frees a frozen unit, but it takes ×1.5 damage.
    if (unit.frozen !== 0) { damage = Math.trunc((damage * 3) / 2); unit.frozen = 0; }
    damageUnit(state, unit, damage, 'explosion');
    const kick = Math.trunc((blow * falloff) / radius);
    unit.vx += Math.trunc((kick * dx) / d);
    unit.vy += Math.trunc((kick * dy) / d) - Math.trunc(((FP >> 1) * knock) / 100);
    unit.walkAcc = 0;
  }
}
